import { useNumbers } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * The account's numbers, and the inbound route each one is on.
 *
 * Read-only for routing. A number is routed by putting it on a route under
 * Inbound routes, where one decision covers every number that shares it
 * (docs/26 D1). A number on no route is flagged here because calls to it are
 * refused -- still the commonest cause of "our new number does not work".
 */
export function NumbersTab({ tenantId, onNavigate }: {
  tenantId: string; onNavigate?: (section: string) => void;
}) {
  const numbers = useNumbers(tenantId);
  const rows = numbers.data?.data ?? [];
  const unrouted = rows.filter((n) => !n.route).length;

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Numbers</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            {rows.length} {rows.length === 1 ? 'number' : 'numbers'}
            {unrouted > 0 ? ` · ${unrouted} on no route` : ''}
          </p>
        </div>
        <div className="arc-spacer" />
        {onNavigate && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => onNavigate('inbound')}>
            Inbound routes
          </button>
        )}
      </div>

      {numbers.error && <ErrorBanner error={numbers.error} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Number</th><th>Type</th><th>Carrier</th><th>Status</th><th>Inbound route</th>
              </tr>
            </thead>
            {numbers.isLoading ? <TableSkeleton cols={5} /> : (
              <tbody>
                {rows.map((n) => (
                  <tr key={n.id}>
                    <td className="arc-mono" style={{ fontWeight: 600 }}>{n.e164}</td>
                    <td className="arc-subtle">{n.number_type.replace(/_/g, ' ')}</td>
                    <td className="arc-muted">{n.carrier_name ?? '—'}</td>
                    <td><StatusBadge status={n.status} /></td>
                    <td>
                      {n.route ? (
                        <span>
                          {n.route.name}
                          {!n.route.is_enabled && <> <Badge tone="warn">off</Badge></>}
                        </span>
                      ) : (
                        <span className="arc-badge arc-badge-warn">
                          <Icon name="warning" size={10} /> no route
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!numbers.isLoading && rows.length === 0 && (
          <Empty icon="hash" title="No numbers assigned"
            hint="Assign a DID from platform inventory to give this account an inbound number." />
        )}
      </div>
    </>
  );
}
